/**
 * ONE TAP — main menu.
 *
 * The wordmark, the name field, CREATE ROOM, and a four-character JOIN code entry
 * that behaves the way a good code entry should: auto-uppercase, auto-advance,
 * backspace-through, arrow-key navigable, paste-aware, and loud about a bad code.
 *
 * Room-code normalisation is implemented here against `ROOM_CODE_ALPHABET` rather
 * than imported, so the menu has no dependency outside `@shared/constants`.
 */

import { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH, ROOM_CODE_PREFIX } from '@shared/constants';
import { getSettings, updateSettings } from './settings';

export interface MenuHooks {
  onCreateRoom(name: string): void;
  onJoinRoom(code: string, name: string): void;
  onOpenSettings(): void;
  onUiSound?(kind: 'hover' | 'click' | 'back' | 'error'): void;
}

const NAME_MAX = 14;

/** `ParentNode` is ambiguous here — @cloudflare/workers-types declares a global one
 *  for HTMLRewriter — so this takes the structural shape it actually needs. */
type Queryable = { querySelector(selectors: string): Element | null };

const q = <T extends HTMLElement>(root: Queryable, sel: string): T => {
  const found = root.querySelector(sel);
  if (!found) throw new Error(`menu: missing element ${sel}`);
  return found as T;
};

/**
 * Fold a typed character into the code alphabet. The alphabet already excludes the
 * ambiguous glyphs (O/0, I/1, S/5), so the only safe fold is the round one: a typed
 * `0` or `O` can only ever have meant `Q`. Everything else outside the alphabet is
 * rejected outright — silently joining the wrong room is far worse than a re-type.
 */
function foldCodeChar(ch: string): string | null {
  const c = ch.toUpperCase();
  const folded = c === '0' || c === 'O' ? 'Q' : c;
  return ROOM_CODE_ALPHABET.includes(folded) ? folded : null;
}

/** Pull a `ONETAP-XXXX`-shaped body out of anything a human might paste. */
export function extractCodeBody(input: string): string {
  let s = (input ?? '').toUpperCase().trim();
  const urlMatch = s.match(/(?:ROOM\/|CODE=|#)([A-Z0-9-]+)/);
  if (urlMatch && urlMatch[1]) s = urlMatch[1];
  s = s.replace(/[^A-Z0-9]/g, '');
  if (s.startsWith('ONETAP')) s = s.slice('ONETAP'.length);
  let out = '';
  for (const ch of s) {
    const f = foldCodeChar(ch);
    if (f) out += f;
    if (out.length >= ROOM_CODE_LENGTH) break;
  }
  return out;
}

const MENU_HTML = `
<div class="ot-menu__bg" aria-hidden="true">
  <div class="ot-bg__sky"></div>
  <div class="ot-bg__sun"></div>
  <div class="ot-bg__skyline">
    <span class="ot-bg__slab ot-bg__slab--1"></span>
    <span class="ot-bg__slab ot-bg__slab--2"></span>
    <span class="ot-bg__slab ot-bg__slab--3"></span>
    <span class="ot-bg__slab ot-bg__slab--4"></span>
    <span class="ot-bg__slab ot-bg__slab--5"></span>
    <!-- ONE saturated plane, and it is a team colour. A second, jade one used to
         sit on the left; jade is an ARENA wayfinding accent, and two unrelated
         coloured rectangles on a menu backdrop read as template decoration. -->
    <span class="ot-bg__accent ot-bg__accent--red"></span>
  </div>
  <div class="ot-bg__haze"></div>
</div>

<div class="ot-menu__inner">
  <header class="ot-wordmark">
    <span class="ot-wordmark__kicker">2v2 &middot; 3v3 &middot; ONE GUN PER TEAM</span>
    <h1 class="ot-wordmark__type">
      <span class="ot-wordmark__one">ONE</span><span class="ot-wordmark__tap">TAP</span>
    </h1>
    <p class="ot-wordmark__sub">CATCH IT TO LOAD IT.</p>
  </header>

  <div class="ot-panel ot-menu__card">
    <label class="ot-namefield" for="ot-name">
      <span class="ot-namefield__label">CALLSIGN</span>
      <input id="ot-name" class="ot-input ot-input--name" type="text" maxlength="${NAME_MAX}"
             autocomplete="off" spellcheck="false" placeholder="ROOKIE" />
    </label>

    <!-- No subtitle. "Spin up a room and share the link" described what a button
         labelled CREATE ROOM was already going to do. DESIGN.md §8: no sentence
         where a fragment works, no fragment where a word works. -->
    <button class="ot-btn ot-btn--hero" type="button" id="ot-create">
      <span class="ot-btn__label">CREATE ROOM</span>
      <span class="ot-btn__spinner" aria-hidden="true"></span>
    </button>

    <div class="ot-or"><span>OR JOIN A FRIEND</span></div>

    <form class="ot-join" id="ot-join-form" novalidate>
      <div class="ot-join__row">
        <span class="ot-join__prefix" aria-hidden="true">${ROOM_CODE_PREFIX}</span>
        <div class="ot-code" id="ot-code" role="group" aria-label="Room code, four characters">
          <input class="ot-code__cell" id="ot-code-0" name="code1" data-i="0" type="text"
                 inputmode="text" maxlength="1" autocapitalize="characters"
                 autocomplete="off" spellcheck="false" aria-label="Code character 1" />
          <input class="ot-code__cell" id="ot-code-1" name="code2" data-i="1" type="text"
                 inputmode="text" maxlength="1" autocapitalize="characters"
                 autocomplete="off" spellcheck="false" aria-label="Code character 2" />
          <input class="ot-code__cell" id="ot-code-2" name="code3" data-i="2" type="text"
                 inputmode="text" maxlength="1" autocapitalize="characters"
                 autocomplete="off" spellcheck="false" aria-label="Code character 3" />
          <input class="ot-code__cell" id="ot-code-3" name="code4" data-i="3" type="text"
                 inputmode="text" maxlength="1" autocapitalize="characters"
                 autocomplete="off" spellcheck="false" aria-label="Code character 4" />
        </div>
        <button class="ot-btn ot-btn--join" type="submit" id="ot-join" disabled>JOIN</button>
      </div>
      <p class="ot-join__msg" id="ot-msg" role="status" aria-live="polite"></p>
    </form>
  </div>

  <!-- No rules block here. It duplicated the lobby's, and a title screen has one
       job: get the player into a room. The lobby teaches the three essentials and
       every player passes through it. See DESIGN.md §8. -->

  <footer class="ot-menu__foot">
    <button class="ot-btn ot-btn--ghost" type="button" id="ot-menu-settings">SETTINGS</button>
    <span class="ot-conn" id="ot-menu-conn" role="status" aria-live="polite"></span>
  </footer>
</div>`;

export class MenuScreen {
  readonly element: HTMLElement;

  private readonly hooks: MenuHooks;
  private readonly nameInput: HTMLInputElement;
  private readonly createBtn: HTMLButtonElement;
  private readonly joinBtn: HTMLButtonElement;
  private readonly joinForm: HTMLFormElement;
  private readonly codeWrap: HTMLElement;
  private readonly cells: HTMLInputElement[];
  private readonly msg: HTMLElement;
  private readonly conn: HTMLElement;

  private busy = false;
  private msgTimer = 0;

  constructor(hooks: MenuHooks) {
    this.hooks = hooks;

    const el = document.createElement('section');
    el.className = 'ot-screen ot-menu';
    el.hidden = true;
    el.innerHTML = MENU_HTML;
    this.element = el;

    this.nameInput = q<HTMLInputElement>(el, '#ot-name');
    this.createBtn = q<HTMLButtonElement>(el, '#ot-create');
    this.joinBtn = q<HTMLButtonElement>(el, '#ot-join');
    this.joinForm = q<HTMLFormElement>(el, '#ot-join-form');
    this.codeWrap = q(el, '#ot-code');
    this.cells = Array.from(el.querySelectorAll<HTMLInputElement>('.ot-code__cell'));
    this.msg = q(el, '#ot-msg');
    this.conn = q(el, '#ot-menu-conn');

    this.nameInput.value = getSettings().name;

    this.nameInput.addEventListener('input', () => {
      // Keep names printable and short; the HUD budgets a fixed width for them.
      const cleaned = this.nameInput.value.replace(/[\u0000-\u001f\u007f\u200b-\u200f\ufeff]/g, '').slice(0, NAME_MAX);
      if (cleaned !== this.nameInput.value) this.nameInput.value = cleaned;
    });
    this.nameInput.addEventListener('change', () => updateSettings({ name: this.name() }));
    this.nameInput.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        this.createBtn.focus();
      }
    });

    this.createBtn.addEventListener('click', () => {
      if (this.busy) return;
      this.hooks.onUiSound?.('click');
      updateSettings({ name: this.name() });
      this.setError(null);
      this.hooks.onCreateRoom(this.name());
    });

    this.joinForm.addEventListener('submit', (ev) => {
      ev.preventDefault();
      this.submitJoin();
    });

    q<HTMLButtonElement>(el, '#ot-menu-settings').addEventListener('click', () => {
      this.hooks.onUiSound?.('click');
      this.hooks.onOpenSettings();
    });

    for (const b of el.querySelectorAll<HTMLElement>('.ot-btn')) {
      b.addEventListener('pointerenter', () => this.hooks.onUiSound?.('hover'));
    }

    this.wireCodeCells();
  }

  // -------------------------------------------------------------------------
  // Public surface
  // -------------------------------------------------------------------------

  show(): void {
    this.element.hidden = false;
    this.element.classList.add('is-entering');
    window.setTimeout(() => this.element.classList.remove('is-entering'), 700);
    this.setBusy(false);
    // Nudge the player toward the first thing they need to fill in.
    if (!this.nameInput.value) this.nameInput.focus();
  }

  hide(): void {
    this.element.hidden = true;
  }

  name(): string {
    const raw = this.nameInput.value.trim();
    return raw.length > 0 ? raw.slice(0, NAME_MAX) : 'ROOKIE';
  }

  /** Full `ONETAP-XXXX` if the four cells are filled, else null. */
  code(): string | null {
    const body = this.cells.map((c) => c.value).join('');
    return body.length === ROOM_CODE_LENGTH ? ROOM_CODE_PREFIX + body : null;
  }

  /** Prefill from a deep link (`#ONETAP-ABCD`) and park focus on JOIN. */
  prefillCode(code: string): void {
    this.fillCode(extractCodeBody(code));
    if (this.code()) this.joinBtn.focus();
  }

  setBusy(busy: boolean, label?: string): void {
    this.busy = busy;
    this.element.classList.toggle('is-busy', busy);
    this.createBtn.disabled = busy;
    this.createBtn.classList.toggle('is-loading', busy);
    q(this.createBtn, '.ot-btn__label').textContent = busy ? (label ?? 'CREATING…') : 'CREATE ROOM';
    this.syncJoinEnabled();
  }

  setError(message: string | null): void {
    window.clearTimeout(this.msgTimer);
    this.msg.textContent = message ?? '';
    this.msg.classList.toggle('is-error', message !== null);
    if (message) {
      this.hooks.onUiSound?.('error');
      this.codeWrap.classList.remove('is-rejected');
      void this.codeWrap.offsetWidth;
      this.codeWrap.classList.add('is-rejected');
      this.msgTimer = window.setTimeout(() => this.codeWrap.classList.remove('is-rejected'), 600);
    }
  }

  setConnection(state: 'idle' | 'connecting' | 'connected' | 'error' | 'closed', msg?: string): void {
    const label =
      state === 'connecting'
        ? 'CONNECTING…'
        : state === 'connected'
          ? 'CONNECTED'
          : state === 'error'
            ? (msg ?? 'CONNECTION FAILED')
            : state === 'closed'
              ? (msg ?? 'DISCONNECTED')
              : '';
    this.conn.textContent = label;
    this.conn.dataset.state = state;
    if (state === 'error' || state === 'closed') {
      this.setBusy(false);
      if (msg) this.setError(msg);
    }
  }

  dispose(): void {
    window.clearTimeout(this.msgTimer);
    this.element.remove();
  }

  // -------------------------------------------------------------------------
  // Code entry
  // -------------------------------------------------------------------------

  private wireCodeCells(): void {
    this.cells.forEach((cell, i) => {
      cell.addEventListener('beforeinput', (ev: Event) => {
        const e = ev as InputEvent;
        if (!e.data) return; // deletions and composition fall through untouched
        e.preventDefault();
        const folded = extractCodeBody(e.data);
        if (folded.length === 0) {
          this.rejectChar(cell);
          return;
        }
        if (folded.length > 1) {
          // Multi-character insert (mobile autofill, IME commit) — spread it.
          this.spreadFrom(i, folded);
          return;
        }
        cell.value = folded;
        this.afterCellFilled(i);
      });

      cell.addEventListener('input', () => {
        // Safety net for input paths that skip beforeinput.
        const folded = extractCodeBody(cell.value);
        cell.value = folded.slice(0, 1);
        this.syncJoinEnabled();
      });

      cell.addEventListener('keydown', (ev) => {
        switch (ev.key) {
          case 'Backspace':
            if (cell.value === '' && i > 0) {
              ev.preventDefault();
              const prev = this.cells[i - 1]!;
              prev.value = '';
              prev.focus();
              prev.select();
              this.syncJoinEnabled();
            }
            break;
          case 'Delete':
            cell.value = '';
            this.syncJoinEnabled();
            break;
          case 'ArrowLeft':
            if (i > 0) {
              ev.preventDefault();
              this.focusCell(i - 1);
            }
            break;
          case 'ArrowRight':
            if (i < this.cells.length - 1) {
              ev.preventDefault();
              this.focusCell(i + 1);
            }
            break;
          case 'Home':
            ev.preventDefault();
            this.focusCell(0);
            break;
          case 'End':
            ev.preventDefault();
            this.focusCell(this.cells.length - 1);
            break;
          case 'Enter':
            ev.preventDefault();
            this.submitJoin();
            break;
          default:
            break;
        }
      });

      cell.addEventListener('paste', (ev) => {
        ev.preventDefault();
        const text = ev.clipboardData?.getData('text') ?? '';
        const body = extractCodeBody(text);
        if (body.length === 0) {
          this.rejectChar(cell);
          this.setError('NOT A ROOM CODE');
          return;
        }
        this.spreadFrom(i, body);
      });

      cell.addEventListener('focus', () => cell.select());
    });

    // Clicking anywhere in the group lands you on the first empty cell.
    this.codeWrap.addEventListener('pointerdown', (ev) => {
      if (ev.target !== this.codeWrap) return;
      ev.preventDefault();
      this.focusFirstEmpty();
    });
  }

  private spreadFrom(start: number, body: string): void {
    let i = start;
    for (const ch of body) {
      if (i >= this.cells.length) break;
      this.cells[i]!.value = ch;
      i++;
    }
    this.afterCellFilled(i - 1);
  }

  private fillCode(body: string): void {
    this.cells.forEach((c, i) => {
      c.value = body[i] ?? '';
    });
    this.syncJoinEnabled();
  }

  private afterCellFilled(i: number): void {
    this.setError(null);
    if (i < this.cells.length - 1) this.focusCell(i + 1);
    this.syncJoinEnabled();
    if (this.code()) {
      this.codeWrap.classList.remove('is-complete');
      void this.codeWrap.offsetWidth;
      this.codeWrap.classList.add('is-complete');
      this.joinBtn.focus();
    }
  }

  private focusCell(i: number): void {
    const c = this.cells[i];
    if (!c) return;
    c.focus();
    c.select();
  }

  private focusFirstEmpty(): void {
    const idx = this.cells.findIndex((c) => c.value === '');
    this.focusCell(idx === -1 ? this.cells.length - 1 : idx);
  }

  private rejectChar(cell: HTMLElement): void {
    cell.classList.remove('is-bad');
    void cell.offsetWidth;
    cell.classList.add('is-bad');
    this.hooks.onUiSound?.('error');
    window.setTimeout(() => cell.classList.remove('is-bad'), 400);
  }

  private syncJoinEnabled(): void {
    const ok = this.code() !== null && !this.busy;
    this.joinBtn.disabled = !ok;
    this.codeWrap.classList.toggle('is-filled', this.code() !== null);
  }

  private submitJoin(): void {
    if (this.busy) return;
    const code = this.code();
    if (!code) {
      this.setError(`ENTER ALL ${ROOM_CODE_LENGTH} CHARACTERS`);
      this.focusFirstEmpty();
      return;
    }
    this.hooks.onUiSound?.('click');
    updateSettings({ name: this.name() });
    this.setError(null);
    this.hooks.onJoinRoom(code, this.name());
  }
}
